// Chord shapes for the EasyPlay 1s — straight from the printed practice
// sheet. The master pattern: any every-other-button line in reading order
// (wrapping across rows) is a chord, and because the instrument transposes
// itself, every shape works in all twelve keys.
//
// Cells are movable-do positions { degree, octave, accidental } matching the
// button grid: octave 0 is the top row's scale, sharps are the small gray
// buttons. Flats are spelled as the sharp below (b3 lives on the #2 button)
// with an `as` label for display.

import { degreeToMidi, noteKey } from "./jianpu";

const c = (degree, octave, accidental = 0, as = null) => ({
  degree,
  octave,
  accidental,
  as,
});

export const CHORD_SHAPES = [
  // ----- page 1: chords on the big buttons alone -----
  {
    id: "I",
    numeral: "I",
    name: "major",
    page: 1,
    blurb: "Top row, skip one. Home base.",
    cells: [c(1, 0), c(3, 0), c(5, 0)],
  },
  {
    id: "vi",
    numeral: "vi",
    name: "minor",
    page: 1,
    blurb: "Middle row, same shape.",
    cells: [c(6, 0), c(1, 1), c(3, 1)],
  },
  {
    id: "IV",
    numeral: "IV",
    name: "major",
    page: 1,
    blurb: "Bottom row, same shape.",
    cells: [c(4, 1), c(6, 1), c(1, 2)],
  },
  {
    id: "V",
    numeral: "V",
    name: "major",
    page: 1,
    blurb: "Starts on the heart button.",
    cells: [c(5, 0), c(7, 0), c(2, 1)],
  },
  {
    id: "V7",
    numeral: "V7",
    name: "dominant seventh",
    page: 1,
    blurb: "V plus one more skip. Pulls home to I.",
    cells: [c(5, 0), c(7, 0), c(2, 1), c(4, 1)],
  },
  {
    id: "vi7",
    numeral: "vi7",
    name: "minor seventh",
    page: 1,
    blurb: "Middle row plus the invader.",
    cells: [c(6, 0), c(1, 1), c(3, 1), c(5, 1)],
  },
  {
    id: "ii",
    numeral: "ii",
    name: "minor",
    page: 1,
    blurb: "Skip-shape from 2, wraps down.",
    cells: [c(2, 0), c(4, 0), c(6, 0)],
  },
  {
    id: "Iadd9",
    numeral: "I add9",
    name: "major add 9",
    page: 1,
    blurb: "Home chord + 2 up top. Shimmer.",
    cells: [c(1, 0), c(3, 0), c(5, 0), c(2, 1)],
  },
  // ----- page 2: borrowed colors (small gray buttons) -----
  {
    id: "i",
    numeral: "i",
    name: "minor home",
    page: 2,
    blurb: "I with a flat 3. Instant mood flip.",
    cells: [c(1, 0), c(2, 0, 1, "b3"), c(5, 0)],
  },
  {
    id: "I7",
    numeral: "I7",
    name: "bluesy seventh",
    page: 2,
    blurb: "Home chord + b7. Pure blues.",
    cells: [c(1, 0), c(3, 0), c(5, 0), c(6, 0, 1, "b7")],
  },
  {
    id: "iv",
    numeral: "iv",
    name: "borrowed minor",
    page: 2,
    blurb: "The heartbreak chord. Try IV then iv.",
    cells: [c(4, 0), c(5, 0, 1, "b6"), c(1, 1)],
  },
  {
    id: "III",
    numeral: "III",
    name: "major drama",
    page: 2,
    blurb: "Same small button, now called #5. Leads to vi.",
    cells: [c(3, 0), c(5, 0, 1, "#5"), c(7, 0)],
  },
  {
    id: "bVII",
    numeral: "bVII",
    name: "rock major",
    page: 2,
    blurb: "The classic rock swagger chord.",
    cells: [c(6, 0, 1, "b7"), c(2, 1), c(4, 1)],
  },
  {
    id: "bVI",
    numeral: "bVI",
    name: "epic major",
    page: 2,
    blurb: "Big movie-trailer sound.",
    cells: [c(5, 0, 1, "b6"), c(1, 1), c(2, 1, 1, "b3")],
  },
  {
    id: "bIII",
    numeral: "bIII",
    name: "minor-key rock",
    page: 2,
    blurb: "Two smalls, one big.",
    cells: [c(2, 0, 1, "b3"), c(5, 0), c(6, 0, 1, "b7")],
  },
  {
    id: "i7",
    numeral: "i7",
    name: "smoky minor seventh",
    page: 2,
    blurb: "Smoky minor seventh on home.",
    cells: [c(1, 0), c(2, 0, 1, "b3"), c(5, 0), c(6, 0, 1, "b7")],
  },
];

export function getChord(id) {
  return CHORD_SHAPES.find((s) => s.id === id) || null;
}

export const PROGRESSIONS = [
  {
    id: "pop",
    name: "The pop loop",
    chords: ["I", "vi", "IV", "V"],
    blurb: "Hundreds of songs. Three rows + the heart shape.",
  },
  {
    id: "doo-wop",
    name: "The 50s doo-wop",
    chords: ["I", "vi", "ii", "V7"],
    blurb: "Stand By Me. Feel the V7 pull home.",
  },
  {
    id: "creep",
    name: "The Creep loop",
    chords: ["I", "III", "IV", "iv"],
    blurb: "Radiohead's Creep. III lifts, iv breaks your heart.",
  },
  {
    id: "classic-rock",
    name: "Classic rock",
    chords: ["I", "bVII", "IV"],
    blurb: "Instant stadium. Loop it forever.",
  },
  {
    id: "epic-minor",
    name: "Epic minor",
    chords: ["i", "bVI", "bIII", "bVII"],
    blurb: "Movie-trailer minor loop, all borrowed shapes.",
  },
  {
    id: "jazzy",
    name: "Jazzy turnaround",
    chords: ["ii", "V7", "Iadd9"],
    blurb: "The classic 2-5-1 with a shimmery landing.",
  },
];

export const WORKOUT = [
  "Rows drill — top row chord, middle row, bottom row. Four slow beats each, no gaps.",
  "Slide drill — walk the skip-one shape up one button at a time from the red button. That plays every chord in the key.",
  "Mood flip — alternate I and i (just swap 3 for its small button). Then IV and iv. Hear major turn to minor.",
  "Resolve drill — alternate V7 and I, then I7 and IV. Learn what tension and release feel like.",
  "Creep loop — I, III, IV, iv, four beats each. Notice one small button doing two different jobs.",
  "Roll drill — arpeggiate the epic minor loop low-to-high, one note per beat.",
];

// Display label for a cell: its flat/sharp spelling when it has one.
export function cellLabel(cell) {
  if (cell.as) return cell.as;
  return `${cell.accidental === 1 ? "#" : ""}${cell.degree}`;
}

// Chord tones as sorted MIDI notes for audio preview.
export function chordMidis(chord, tonicMidi) {
  return chord.cells
    .map((cell) => degreeToMidi(tonicMidi, cell.degree, cell.octave, cell.accidental))
    .sort((a, b) => a - b);
}

// Collect-the-chord practice logic: notes arrive one at a time (held chord,
// rolled arpeggio, or on-screen taps all look the same). A chord tone joins
// the pile; a stray note spills it and you start the chord over.
//   advanceOnNote(accumSet, chord, playedKey) →
//     { accum, complete, wrong }
export function advanceOnNote(accum, chord, playedKey) {
  const wanted = new Set(chord.cells.map(noteKey));
  if (!wanted.has(playedKey)) {
    return { accum: new Set(), complete: false, wrong: true };
  }
  const next = new Set(accum);
  next.add(playedKey);
  const complete = chord.cells.every((cell) => next.has(noteKey(cell)));
  return { accum: next, complete, wrong: false };
}
